"use client";

import { useContext, useState, useEffect } from "react";
import { NavbarMenu, NavbarMenuMobile } from "../../mock_data/data";
import { motion } from "framer-motion";
import LoginButton from "../LoginButton/LoginButton";
import { useLocation, useNavigate } from "react-router-dom";
import { MainContext } from "@/context/MainContext";
import GlassmorphicNav from "@/components/Navbar/TabNavbar";
import { cdnPath } from "@/utility/api";

const Navbar = ({ showBottomNavBar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn } = useContext(MainContext);
  const [activeTab, setActiveTab] = useState("home");

  // Effect to update active tab based on current route
  useEffect(() => {
    const path = location.pathname;
    const currentTab = NavbarMenu.find((item) => item.url === path);
    if (currentTab) {
      setActiveTab(currentTab.name.toLowerCase());
    } else if (path === "/profile") {
      setActiveTab("profile");
    }
  }, [location.pathname]);

  const handleTabChange = (tab) => {
    console.log("Tab clicked:", tab);
    setActiveTab(tab);

    if (tab === "profile") {
      if (isLoggedIn) {
        const profileMenuItem = NavbarMenuMobile.find(
          (item) =>
            item.name.toLowerCase().replace(/\s+/g, "") ===
            tab.toLowerCase().replace(/\s+/g, "")
        );
      }
      return;
    }

    const menuItem = NavbarMenu.find((item) => item.name.toLowerCase() === tab);

    console.log("No matching route found");
    console.log(
      "Available mobile menu items:",
      NavbarMenuMobile.map((item) => item.name)
    );
  };

  return (
    <>
      {/* Desktop Navbar */}
      <motion.nav
        initial={{ y: -50 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="fixed top-0 left-0 right-0 z-40 shadow-md backdrop-blur-lg bg-opacity-80"
      >
        {/* Mobile Layout (< 1024px) */}
        <div className="container mx-auto flex items-center justify-between px-4 py-3 bg-transparent lg:hidden">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2">
            <img src={`${cdnPath}/logo.png`} alt="Logo" className="w-[100px]" />
          </a>

          {/* Bring Us To Campus Button */}
          <div className="relative inline-block">
            <a
              href="/bring-us-to-campus"
              className="px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-full shadow-md hover:from-blue-700 hover:to-blue-600 transition-all duration-300 whitespace-nowrap"
            >
              Bring Us To Campus
            </a>
          </div>
        </div>

        {/* Desktop Layout (>= 1024px) */}
        <div className="hidden lg:flex container mx-auto px-4 py-3 bg-transparent">
          {/* Logo - Fixed width */}
          <div className="flex items-center" style={{ width: "280px" }}>
            <a href="/" className="flex items-center gap-2">
              <img
                src={`${cdnPath}/logo.png`}
                alt="Logo"
                className="w-[100px] xl:w-[120px]"
              />
            </a>
          </div>

          {/* Center Menu - Absolutely centered */}
          <div className="flex-1 flex items-center justify-center">
            <ul className="flex items-center justify-center gap-4 bg-black/70 rounded-full py-2 px-8 shadow-sm">
              {NavbarMenu.map((item) => {
                if (item.name === "Session") {
                  return (
                    <li key={item.id} className="relative group">
                      <a
                        href="/products"
                        className={`text-sm xl:text-base font-semibold text-zinc-200 px-3 py-1 hover:text-tertiary-100 transition-all duration-300 ease-in-out flex items-center gap-1 ${
                          location.pathname === item.url
                            ? "border-b-2 border-tertiary-100 px-1 pr-2"
                            : ""
                        }`}
                      >
                        Resources 
                        {/* <span className="text-xs">▼</span> */}
                      </a>
                      {/* <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-black/90 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                        <div className="py-1">
                          <a
                            href="/products"
                            className="block px-4 py-2 text-sm text-zinc-200 hover:bg-gray-800"
                          >
                            Product
                          </a>
                          <a
                            href="/bookcall/101"
                            className="block px-4 py-2 text-sm text-zinc-200 hover:bg-gray-800"
                          >
                            Session
                          </a>
                        </div>
                      </div> */}
                    </li>
                  );
                }
                const displayName =
                  item.name === "Products" ? "Programs" : item.name;
                return (
                  <li key={item.id}>
                    <a
                      href={item.url}
                      className={`text-sm xl:text-base font-semibold text-zinc-200 px-3 py-1 hover:text-tertiary-100 transition-all duration-300 ease-in-out ${
                        location.pathname === item.url
                          ? "border-b-2 border-tertiary-100 px-1 pr-2"
                          : ""
                      }`}
                    >
                      {displayName}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right Buttons - Fixed width to match logo */}
          <div
            className="flex items-center justify-end gap-3"
            style={{ width: "280px" }}
          >
            <div class="relative inline-block">
              <a
                href="/bring-us-to-campus"
                className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-full shadow-md hover:from-blue-700 hover:to-blue-600 transition-all duration-300 whitespace-nowrap"
              >
                Bring Us To Campus
              </a>
            </div>
            <LoginButton />
          </div>
        </div>
      </motion.nav>

      {/* Mobile Bottom Navigation */}
      {showBottomNavBar && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50">
          <GlassmorphicNav
            initialTab={activeTab}
            onTabChange={(tab) => {
              console.log("GlassmorphicNav tab clicked:", tab);
              handleTabChange(tab);
            }}
            glassOpacity={95}
            customColors={{
              home: "#60A5FA",
              resume: "#A78BFA",
              session: "#34D399",
              courses: "#FBBF24",
              profile: "#F87171",
            }}
          />
        </div>
      )}

      {/* Add padding to main content for mobile */}
      <style jsx global>{`
        main {
          padding-bottom: 120px;
        }
        @media (min-width: 1024px) {
          main {
            padding-bottom: 0;
          }
        }
      `}</style>
    </>
  );
};

export default Navbar;
